import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, Button, Box, Text, VStack, HStack, 
  SimpleGrid, Divider, Badge
} from '@chakra-ui/react';
import React, { useState } from 'react';
import type { Dictamen } from './types';
import ModalHistorialDictamen from './ModalHistorialDictamen';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  dictamen: Dictamen | null;
}

export default function ModalDetalleDictamen({ isOpen, onClose, dictamen }: Props) {
  const [modalHistorialOpen, setModalHistorialOpen] = useState(false);
  
  if (!dictamen) return null;

  const getTipoColor = (tipo: string) => {
    const tipoLower = tipo?.toLowerCase() || '';
    if (tipoLower.includes('riesgo')) return '#741c35ff';
    if (tipoLower.includes('seguridad')) return '#291e20ff';
    if (tipoLower.includes('habitabilidad')) return '#bb8c52';
    return '#bb8c52';
  };

  const renderMapaUbicacion = () => {
    if (dictamen.latitud && dictamen.longitud) {
      return (
        <Box>
          <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={2}>
            Ubicación
          </Text>
          <Box borderRadius="md" overflow="hidden" h="200px" bg="gray.100">
            <iframe
              width="100%"
              height="100%"
              frameBorder="0"
              src={`https://www.google.com/maps?q=${dictamen.latitud},${dictamen.longitud}&output=embed`}
              allowFullScreen
            />
          </Box>
        </Box>
      );
    }
    return (
      <Box>
        <Text fontWeight="semibold" fontSize="sm" color="gray.700">
          Ubicación
        </Text>
        <Text fontSize="sm" color="gray.500">No hay coordenadas disponibles</Text>
      </Box>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxH="90vh" overflowY="auto">
        <ModalHeader
          bgImage="url('/img/bg.webp')"
          bgSize="cover"
          bgPosition="center"
          bgRepeat="no-repeat"
          color="#5b182b"
          fontWeight="bold"
          position="relative"
          textAlign="left"
          py={4}
        >
          <VStack spacing={1} align="start">
            <Text fontSize="xl" fontWeight="bold" color="#ffffffff">
              {dictamen.folio || `Dictamen #${dictamen.id}`}
            </Text>
            <Badge bg={getTipoColor(dictamen.tipo || '')} color="white" px={3} py={1} borderRadius="full">
              {dictamen.tipo || 'Sin tipo'}
            </Badge>
          </VStack>
        </ModalHeader>
        <ModalCloseButton color="white" />
        
        <ModalBody p={6}>
          <VStack spacing={6} align="stretch">
            
            {/* Información Básica */}
            <Box>
              <Text fontSize="lg" fontWeight="bold" color={getTipoColor(dictamen.tipo || '')} mb={4}>
                Información General
              </Text>
              <SimpleGrid columns={2} spacing={4}>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                    Nombre/Razón Social del Solicitante
                  </Text>
                  <Text fontSize="md">{dictamen.nombre || 'N/A'}</Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                    Operador
                  </Text>
                  <Text fontSize="md">{dictamen.operador || 'N/A'}</Text>
                </Box>
              </SimpleGrid>
            </Box>

            <Divider />

            {/* Dirección y Ubicación */}
            <Box>
              <Text fontSize="lg" fontWeight="bold" color={getTipoColor(dictamen.tipo || '')} mb={4}>
                Ubicación
              </Text>
              <VStack spacing={4} align="stretch">
                <SimpleGrid columns={2} spacing={4}>
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                      Dirección
                    </Text>
                    <Text fontSize="md">{dictamen.direccion || 'N/A'}</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                      Colonia
                    </Text>
                    <Text fontSize="md">{dictamen.colonia || 'N/A'}</Text>
                  </Box>
                </SimpleGrid>
                {renderMapaUbicacion()}
              </VStack>
            </Box>

            <Divider />

            {/* Información Específica por Tipo */}
            {dictamen.tipo?.toLowerCase().includes('riesgo') && dictamen.dictamenRiesgo && (
              <Box>
                <Text fontSize="lg" fontWeight="bold" color={getTipoColor(dictamen.tipo)} mb={4}>
                  Información del Dictamen de Riesgo
                </Text>
                <SimpleGrid columns={2} spacing={4}>
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                      Superficie del Inmueble (m²)
                    </Text>
                    <Text fontSize="md">{dictamen.dictamenRiesgo.superficie || 'N/A'}</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                      Aforo Permitido
                    </Text>
                    <Text fontSize="md">{dictamen.dictamenRiesgo.aforo || 'N/A'} personas</Text>
                  </Box>
                </SimpleGrid>
                <Box mt={4}>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                    Observaciones
                  </Text>
                  <Text fontSize="md">{dictamen.observaciones || 'Sin observaciones'}</Text>
                </Box>
              </Box>
            )}

            {dictamen.tipo?.toLowerCase().includes('seguridad') && dictamen.dictamenSeguridad && (
              <Box>
                <Text fontSize="lg" fontWeight="bold" color={getTipoColor(dictamen.tipo)} mb={4}>
                  Información del Dictamen de Seguridad
                </Text>
                <SimpleGrid columns={2} spacing={4}>
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                      Denominación del Negocio
                    </Text>
                    <Text fontSize="md">{dictamen.dictamenSeguridad.denominacionNegocio || 'N/A'}</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                      Giro
                    </Text>
                    <Text fontSize="md">{dictamen.dictamenSeguridad.giro || 'N/A'}</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                      Medidas Mínimas de Seguridad
                    </Text>
                    <Badge 
                      colorScheme={dictamen.dictamenSeguridad.medidasMinimas ? 'green' : 'red'}
                      variant="subtle"
                    >
                      {dictamen.dictamenSeguridad.medidasMinimas ? 'Sí cumple' : 'No cumple'}
                    </Badge>
                  </Box>
                </SimpleGrid>
                <Box mt={4}>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                    Observaciones
                  </Text>
                  <Text fontSize="md">{dictamen.observaciones || 'Sin observaciones'}</Text>
                </Box>
              </Box>
            )}

            {dictamen.tipo?.toLowerCase().includes('habitabilidad') && dictamen.dictamenHabitabilidad && (
              <Box>
                <Text fontSize="lg" fontWeight="bold" color={getTipoColor(dictamen.tipo)} mb={4}>
                  Información del Dictamen de Habitabilidad
                </Text>
                <SimpleGrid columns={2} spacing={4}>
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                      Tipo de Inmueble
                    </Text>
                    <Text fontSize="md">{dictamen.dictamenHabitabilidad.tipoInmuebleCatalogo?.descripcion || 'N/A'}</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                      Superficie Construida (m²)
                    </Text>
                    <Text fontSize="md">{dictamen.dictamenHabitabilidad.superficeConstruida || 'N/A'}</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                      Niveles
                    </Text>
                    <Text fontSize="md">{dictamen.dictamenHabitabilidad.niveles || 'N/A'}</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                      Referencia del Inmueble
                    </Text>
                    <Text fontSize="md">{dictamen.dictamenHabitabilidad.referenciaInmueble || 'N/A'}</Text>
                  </Box>
                </SimpleGrid>
                <Box mt={4}>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                    Observaciones
                  </Text>
                  <Text fontSize="md">{dictamen.observaciones || 'Sin observaciones'}</Text>
                </Box>
              </Box>
            )}

            <Divider />

            {/* Estados y Fechas */}
            <Box>
              <Text fontSize="lg" fontWeight="bold" color={getTipoColor(dictamen.tipo || '')} mb={4}>
                Estado del Dictamen
              </Text>
              <SimpleGrid columns={3} spacing={4}>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                    Estado de Entrega
                  </Text>
                  <Text fontSize="md">{dictamen.estatusEntrega || 'Pendiente'}</Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                    Estado de Pago
                  </Text>
                  <Text fontSize="md">{dictamen.estatusPago || 'No pagado'}</Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                    Monto
                  </Text>
                  <Text fontSize="md" fontWeight="bold" color="#bb8c52">
                    ${Number(dictamen.montoPago ?? 0).toFixed(2)}
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>

          </VStack>
        </ModalBody>
        
        <ModalFooter>
          <HStack spacing={3}>
            <Button
              bg="#bb8c52"
              color="white"
              _hover={{ bg: "#a67c47" }}
              onClick={() => setModalHistorialOpen(true)}
            >
              Consultar Histórico
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cerrar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
      
      <ModalHistorialDictamen
        isOpen={modalHistorialOpen}
        onClose={() => setModalHistorialOpen(false)}
        dictamenId={dictamen.id}
      />
    </Modal>
  );
}
